import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import {
  Box,
  CircularProgress,
  TableBody as MuiTableBody,
  TableHead,
  TableRow,
  tableRowClasses,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  CellContext,
  Column,
  flexRender,
  HeaderContext,
  Row,
  Table,
} from '@tanstack/react-table';
import React, { Fragment, PropsWithChildren, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { CellStyle } from '../../../react-table';
import { borderStyleToString } from '../../../utils/border-style';

import {
  RenderTableProps,
  TableAndElementInformation,
  TableInformation,
  RendererProps,
} from './PresentationalTable';
import {
  HscanTable,
  HscanTableCell,
  HscanTableCheckbox,
  HscanTableCheckedCountText,
  HscanTableFooterContainer,
  HscanTablePagination,
} from './Table';
import { Order } from './types';

export const handleCellStyle = (style: CellStyle | undefined) => {
  if (!style) {
    return;
  }

  const hscanTableCellProps: { [key: string]: unknown } = {
    align: style.align,
    $background: style.background,
    $border: borderStyleToString(style.border),
    $borderTop: borderStyleToString(style.borderTop, 'top'),
    $borderRight: borderStyleToString(style.borderRight, 'right'),
    $borderBottom: borderStyleToString(style.borderBottom, 'bottom'),
    $borderLeft: borderStyleToString(style.borderLeft, 'left'),
    $isNonTextContent: style?.isNonTextContent,
  };

  for (const key in hscanTableCellProps) {
    if (hscanTableCellProps[key] === undefined) {
      delete hscanTableCellProps[key];
    }
  }

  return hscanTableCellProps;
};

interface SortableTableHeadProps<D> {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof D) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  table: Table<D>;
  disableSelect?: boolean;
  disableSelectAll?: boolean;
}
function SortableTableHead<D>(props: SortableTableHeadProps<D>) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    table,
    onRequestSort,
    disableSelect = false,
    disableSelectAll = false,
  } = props;
  const createSortHandler =
    (property: keyof D) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableRow>
      {!disableSelect && (
        <HscanTableCell padding="checkbox">
          {!disableSelectAll && (
            <HscanTableCheckbox
              indeterminateIcon={<IndeterminateCheckBoxOutlinedIcon />}
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checkedIcon={<CheckBoxOutlinedIcon />}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          )}
        </HscanTableCell>
      )}
      {table.getLeafHeaders().map(header => (
        <HscanTableCell
          key={header.id}
          {...handleCellStyle(header.column.columnDef.meta?.headerStyle)}
          sortDirection={
            header.column.columnDef.meta?.sortable
              ? orderBy === header.id
                ? order
                : false
              : undefined
          }>
          {header.column.columnDef.meta?.sortable ? (
            <TableSortLabel
              active={orderBy === header.id}
              direction={orderBy === header.id ? order : 'asc'}
              onClick={createSortHandler(header.id as keyof D)}>
              <>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {orderBy === header.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </>
            </TableSortLabel>
          ) : (
            (flexRender(
              header.column.columnDef.header,
              header.getContext(),
            ) as React.ReactNode)
          )}
        </HscanTableCell>
      ))}
    </TableRow>
  );
}

const SortableTableHeader = <D, K extends keyof D>(
  props: TableInformation<D, K>,
) => {
  const {
    table,
    onSelectAll,
    disableSelect,
    disableSelectAll,
    order,
    orderBy,
    onChangeOrder,
    onChangeOrderBy,
    selectables,
    currentSelected,
  } = props;

  const handleSelectAll = (checked: boolean) => {
    onSelectAll?.(checked, selectables);
  };

  const handleRequestSort = (property: keyof D) => {
    const isAsc = orderBy === property && order === 'asc';
    if (orderBy === property) {
      onChangeOrder?.(isAsc ? 'desc' : 'asc');
    } else {
      onChangeOrderBy?.(property, isAsc ? 'desc' : 'asc');
    }
  };

  return (
    <SortableTableHead
      rowCount={selectables.length}
      numSelected={currentSelected.length}
      order={order}
      orderBy={orderBy as string}
      onSelectAllClick={event => {
        handleSelectAll(event.currentTarget.checked);
      }}
      onRequestSort={(_, property) => handleRequestSort(property)}
      table={table}
      disableSelect={disableSelect}
      disableSelectAll={disableSelectAll}
    />
  );
};

const SortableTableFooter = <D, K extends keyof D>(
  props: TableInformation<D, K>,
) => {
  const {
    totalCount,
    selected,
    countPerPage,
    countPerPageOptions,
    page,
    onChangePage,
    onChangeCountPerPage,
  } = props;

  const { t } = useTranslation();

  return (
    <HscanTableFooterContainer>
      <HscanTableCheckedCountText>
        {selected.length
          ? t('common.table.selectedCount', { count: selected.length })
          : undefined}
      </HscanTableCheckedCountText>
      <HscanTablePagination
        rowsPerPageOptions={countPerPageOptions}
        labelRowsPerPage={t('common.table.countPerPage')}
        component="div"
        count={totalCount}
        rowsPerPage={countPerPage}
        page={page}
        onPageChange={(_, newPage) => onChangePage?.(newPage)}
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          onChangeCountPerPage?.(parseInt(event.target.value, 10));
          onChangePage?.(0);
        }}
      />
    </HscanTableFooterContainer>
  );
};

export const BasicTableElement = <D, K extends keyof D>(
  props: TableAndElementInformation<D, K>,
) => {
  const {
    row,
    identifyKey,
    onSelect,
    onFocusData,
    disableSelect,
    element,
    isSelected,
    isFocused,
    isSelectable,
  } = props;
  return (
    <TableRow
      hover
      onClick={() => {
        onFocusData?.(element);
      }}
      tabIndex={-1}
      key={element[identifyKey] as string}
      selected={isSelected}
      sx={{
        [`&.${tableRowClasses.root}:hover`]: {
          backgroundColor: '#e5e5e5',
        },
        [`&.${tableRowClasses.root}`]: {
          backgroundColor: `${
            isFocused ? 'var(--HScan_Light_Blue, #2e5be02f)' : '#ffffff'
          }`,
        },
        [`&.${tableRowClasses.selected}`]: {
          backgroundColor: `${
            isSelected
              ? isFocused
                ? 'var(--HScan_Light_Blue, #2e5be02f)'
                : 'var(--HScan_BG_Grey, #f5f5f5)'
              : '#ffffff'
          }`,
        },
        [`&.${tableRowClasses.selected}:hover`]: {
          backgroundColor: '#e5e5e5',
        },
      }}>
      {!disableSelect && (
        <HscanTableCell padding="checkbox">
          <HscanTableCheckbox
            checkedIcon={<CheckBoxOutlinedIcon />}
            color="primary"
            disabled={!isSelectable}
            checked={isSelected}
            onChange={event => {
              onSelect?.(element[identifyKey], event.target.checked);
            }}
            onClick={event => event.stopPropagation()}
          />
        </HscanTableCell>
      )}
      {row.getVisibleCells().map(cell => (
        <HscanTableCell
          key={cell.id}
          {...handleCellStyle(cell.column.columnDef.meta?.cellStyle)}>
          {
            flexRender(
              cell.column.columnDef.cell,
              cell.getContext(),
            ) as React.ReactNode
          }
        </HscanTableCell>
      ))}
    </TableRow>
  );
};

export const ElementWrapper = <D, K extends keyof D>(
  props: Omit<RenderTableProps<D, K>, 'ElementProvider'> & {
    Element: (
      props: TableAndElementInformation<D, K>,
    ) => React.ReactElement | null;
  },
) => {
  const { tableContext, elementContext, Element } = props;
  const tableInfo = useContext(tableContext);
  const elementInfo = useContext(elementContext);

  return <Element {...tableInfo} {...elementInfo} />;
};

export const BasicTable = <D, K extends keyof D>(
  props: RenderTableProps<D, K> & RendererProps<D, K>,
) => {
  const {
    tableContext,
    ElementProvider,
    renderEmpty,
    renderLoading,
    Element = BasicTableElement,
    Header = SortableTableHeader,
    Footer = SortableTableFooter,
    TableBody = BasicTableBody,
  } = props;
  const tableInfo = useContext(tableContext);
  const {
    table,
    dataToShow,
    totalCount,
    page,
    countPerPage,
    isLoading,
    identifyKey,

    disableSelect,
  } = tableInfo;

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * countPerPage - totalCount) : 0;
  const spanForCover =
    (table.getVisibleLeafColumns().length ?? 0) + (disableSelect ? 0 : 1);

  return (
    <HscanTable
      head={
        <TableHead>
          <Header {...tableInfo} />
        </TableHead>
      }
      body={
        <TableBody {...tableInfo}>
          {isLoading ? (
            renderLoading?.(tableInfo)
          ) : (
            <>
              {dataToShow.map((element, elementIndex) => (
                <ElementProvider
                  key={`${element[identifyKey]}${elementIndex}`}
                  element={element}
                  elementIndex={elementIndex}>
                  <ElementWrapper {...props} Element={Element} />
                </ElementProvider>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <HscanTableCell colSpan={spanForCover} />
                </TableRow>
              )}
              {dataToShow.length === 0 && renderEmpty?.(tableInfo)}
            </>
          )}
        </TableBody>
      }>
      <Footer {...tableInfo} />
    </HscanTable>
  );
};

export const LoadingIndicator = <D, K extends keyof D>(
  props: TableInformation<D, K>,
) => {
  const { table, disableSelect } = props;
  const spanForCover =
    (table.getVisibleLeafColumns().length ?? 0) + (disableSelect ? 0 : 1);

  return (
    <TableRow>
      <HscanTableCell align="center" colSpan={spanForCover}>
        <CircularProgress />
      </HscanTableCell>
    </TableRow>
  );
};

export const BasicTableBody = ({ children }: PropsWithChildren) => (
  <MuiTableBody>{children}</MuiTableBody>
);

export const FragmentTableBody = ({ children }: PropsWithChildren) => (
  <Fragment>{children}</Fragment>
);

export const renderHeader = <D, K extends keyof D>(
  table: Table<D>,
  row: Row<D>,
  key: K,
) => {
  const column = table.getColumn(key as string) as Column<D, K>;
  const columnIndex = table.getAllColumns().indexOf(column) as number;
  return flexRender(
    column.columnDef.header,
    table.getLeafHeaders()[columnIndex].getContext() as HeaderContext<D, K>,
  );
};

export const renderCell = <D, K extends keyof D>(
  table: Table<D>,
  row: Row<D>,
  key: K,
  props?: object,
) => {
  const column = table.getColumn(key as string) as Column<D, K>;
  const columnIndex = table.getAllColumns().indexOf(column) as number;
  return flexRender(column.columnDef.cell, {
    ...row.getVisibleCells()[columnIndex].getContext(),
    ...props,
  } as CellContext<D, K>);
};
